import { useEffect, useState } from "react";
import {
  Search,
  PlusSquare,
  Edit,
  Trash2,
  StickyNoteIcon,
  Link as LinkIcon,
  Layers,
  XCircle,
} from "lucide-react";
import Pagination from "../../../components/Pagination";
import { useContents } from "../../../hooks/useContents";
import { usePages } from "../../../hooks/usePages";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { toast } from "react-toastify";
import Skeleton from "../../../components/ui/Skeleton";
import DeleteModal from "../../../components/ui/DeleteModal";
import ContentForm from "./ContentForm";
import { useParams } from "react-router-dom";
import Breadcrumb from "../../../components/ui/Breadcrumb";

function ContentList() {
  const [contentsList, setContentsList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedContent, setSelectedContent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // view, edit, create
  const [deleteModal, setDeleteModal] = useState(false);
  const [contentData, setContentData] = useState({
    currentPage: 1,
    totalPages: 1,
    showing: 10,
    total: 0,
  });
  const [currentCMS, setCurrentCMS] = useState({});
  const { id } = useParams();

  const queryClient = useQueryClient();

  const { fetchContents, createContent, deleteContent, updateContent } =
    useContents({
      pagination: { page: contentData.currentPage, limit: 10 },
      sort: { field: "created_at", direction: "desc" },
    });
  const { fetchPageById } = usePages({
    pagination: { page: contentData.currentPage, limit: 10 },
    sort: { field: "created_at", direction: "desc" },
  });

  const contentsQuery = useQuery({
    queryKey: ["contents", contentData.currentPage, id],
    queryFn: () => fetchContents(id),
    keepPreviousData: true,
  });

  const pageQuery = useQuery({
    queryKey: ["page", id],
    queryFn: () => fetchPageById(id),
    enabled: !!id,
    onSuccess: (data) => {
      if (data?.data) {
        setSelectedContent(data.data);
      }
    },
  });

  useEffect(() => {
    if (pageQuery.data) {
      const response = pageQuery.data;
      setCurrentCMS(response?.data || {});
    }
  }, [pageQuery.data]);

  useEffect(() => {
    if (contentsQuery.data) {
      const response = contentsQuery.data;
      setContentsList(response?.data?.contents || []);
      setContentData({
        currentPage: response?.data?.pagination?.page || 1,
        totalPages: response?.data?.pagination?.lastPage || 1,
        total: response?.data?.pagination?.total || 0,
        showing: response?.data?.pagination?.showing || 10,
      });
    }
  }, [contentsQuery.data]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const openModal = (content, mode) => {
    setSelectedContent(content);
    setModalMode(mode);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedContent(null);
  };

  const handlePageChange = (page) => {
    setContentData((prev) => ({
      ...prev,
      currentPage: page,
      showing: 10,
    }));
  };

  const filteredContents = contentsList.filter((content) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        content.title.toLowerCase().includes(query) ||
        (content.path && content.path.toLowerCase().includes(query))
      );
    }
    return true;
  });

  const handleUpdate = (content) => {
    openModal(content, "edit");
  };

  const handleDelete = {
    onDelete: (content) =>
      mutation.deleteContent.mutate(content._id, {
        onSuccess: (data) => {
          toast.success(data.message || "Content Deleted");
          setDeleteModal(false);
        },
        onError: (error) => {
          toast.error(error.message || "Failed to delete content");
        },
      }),
    onClose: () => setDeleteModal(false),
    onOpen: (content) => {
      setDeleteModal(true);
      setSelectedContent(content);
    },
  };

  const mutation = {
    createContent: useMutation({
      mutationFn: (body) => {
        if (modalMode === "edit") {
          return updateContent(selectedContent._id, body);
        }
        return createContent(body);
      },
    }),
    deleteContent: useMutation({
      mutationFn: (id) => deleteContent(id),
    }),
  };

  const handleSubmit = (body) => {
    mutation.createContent.mutate(body, {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(["contents"]);
        toast.success(
          data?.data?.message ||
            `Content ${
              modalMode === "edit" ? "Updated" : "Created"
            } successfully`
        );
        setShowModal(false);
      },
      onError: (error) => {
        toast.error(error.message || "Failed to save content");
      },
    });
  };

  const breadcrumbItems = [
    { label: "CMS", to: "/cms" },
    { label: "Content List", to: "/cms/contents" },
  ];

  return (
    <div>
      <main className="">
        <Breadcrumb items={breadcrumbItems} />
        {/* Header and Add Button */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold">
            {currentCMS?.title} - Content List
          </h1>
          <button
            onClick={() => openModal({}, "create")}
            className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
          >
            <PlusSquare className="mr-2 h-4 w-4" />
            Add New Content
          </button>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search contents..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Table or Skeleton */}
          {contentsQuery.isLoading ? (
            <Skeleton.Card header rows={10} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Title
                    </th>

                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Created At
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Updated At
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContents.length > 0 ? (
                    filteredContents.map((content) => (
                      <tr
                        key={content._id}
                        className="hover:bg-gray-50 border-b"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                              <Layers className="h-4 w-4 text-gray-600" />
                            </div>
                            <div className="ml-3">
                              <div className="font-medium text-gray-900">
                                {content.title}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-500">
                          {moment(content.created_at).format(
                            "MMMM Do YYYY, h:mm:ss a"
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {moment(content.updated_at).format(
                            "MMMM Do YYYY, h:mm:ss a"
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => openModal(content, "view")}
                              className="text-gray-400 hover:text-gray-500"
                              title="View Details"
                            >
                              <StickyNoteIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleUpdate(content)}
                              className="text-blue-400 hover:text-blue-500"
                              title="Edit Content"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              className="text-red-400 hover:text-red-500"
                              onClick={() => handleDelete.onOpen(content)}
                              title="Delete Content"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No contents found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={contentData.currentPage}
            onPageChange={handlePageChange}
            showing={contentData.showing}
            totalPages={contentData.totalPages}
            total={contentData.total}
          />
        </div>
      </main>
      <DeleteModal
        isOpen={deleteModal}
        onClose={handleDelete.onClose}
        onDelete={() => handleDelete.onDelete(selectedContent)}
        title="Content"
      />
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeModal}
          ></div>
          <div className="h-4/5 relative bg-white rounded-2xl max-w-4xl w-full mx-4 shadow-xl overflow-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium">
                {modalMode === "view"
                  ? "Content Details"
                  : modalMode === "edit"
                  ? "Edit Content"
                  : "Create Content"}
              </h3>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                onClick={closeModal}
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <ContentForm
                mode={modalMode}
                defaultValues={
                  selectedContent
                    ? {
                        ...selectedContent,
                        created_at: moment(selectedContent.created_at).format(
                          "YYYY-MM-DD"
                        ),
                        contentIds: selectedContent.contentIds || [],
                      }
                    : {}
                }
                onCancel={closeModal}
                onSubmit={(data) => handleSubmit(data)}
                submissionLoading={mutation.createContent.isLoading}
                CMSData={currentCMS}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContentList;
